/**
 * One-shot effects played through `IEffectsDirector.play(name, params)`. Every effect is a small
 * fixed-step state machine drawn at integer pixels; randomness only through the director's SeededRng;
 * `reduce` (reduce-motion) shortens/steadies flashes and drops screen-space distortion.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import type { SeededRng } from '@/core/rng';
import {
  allPairs, arcPoints, bresenham, clamp, easeInOutSine, easeOutCubic, FACING_ANGLE, glitchSchedule, lerp, lerpColor,
  parseColor, pentagonPoints, southwestLineIndex, trianglePoints, type GlitchBurst, type Pt,
} from './geometry';

export interface FxHost {
  scene: Phaser.Scene;
  rng: SeededRng;
  readonly reduce: boolean;
  sfx(key: string, opts?: { volume?: number; x?: number; y?: number }): void;
  duck(amount: number, ms: number): void;
  /** Per-director singletons (fade plate, filters) so repeated effects update instead of stacking. */
  shared: Map<string, unknown>;
  /** Asks the weather lightning layer to flash now (no-op when not attached). */
  lightning(): void;
}

export interface OneShot {
  /** Keeps its visuals (and keeps stepping) after completion until the director detaches. */
  readonly persistent: boolean;
  /** Live particle counts (tests / HUD). */
  stats?(): Record<string, number>;
  /** Advance by dtMs; return true once complete. */
  step(dt: number): boolean;
  /** Jump to the end state (cinematic skipped / director detaching). */
  finish(): void;
  destroy(): void;
}
export type OneShotFactory = (host: FxHost, params: Record<string, unknown>) => OneShot;

type P = Record<string, unknown>;
const num = (p: P, k: string, d: number): number => { const v = p[k]; return typeof v === 'number' && Number.isFinite(v) ? v : d; };
const str = (p: P, k: string, d: string): string => (typeof p[k] === 'string' ? (p[k] as string) : d);
const bool = (p: P, k: string, d: boolean): boolean => (typeof p[k] === 'boolean' ? (p[k] as boolean) : d);
const q = Math.floor;

function plot(g: Phaser.GameObjects.Graphics, pts: readonly Pt[], color: number, alpha: number, size = 1): void {
  g.fillStyle(color, alpha);
  for (const p of pts) g.fillRect(p.x, p.y, size, size);
}
function screenGraphics(scene: Phaser.Scene, depth: number): Phaser.GameObjects.Graphics { return scene.add.graphics().setScrollFactor(0).setDepth(depth); }

// ------------------------------------------------------------------ ritual-pentagon (P3 无名小岛)
/** 木桩五角 → 两两连线 → 念叨 → 一根线亮起（朝西南）. Persistent. */
class RitualPentagon implements OneShot {
  readonly persistent = true;
  private readonly base: Phaser.GameObjects.Graphics;
  private readonly glow: Phaser.GameObjects.Graphics;
  private readonly stakes: Pt[]; private readonly pairs: [number, number][]; private readonly lines: Pt[][];
  private readonly glowIndex: number;
  private readonly stakeMs: number; private readonly lineMs: number; private readonly chantMs: number; private readonly glowMs: number; private readonly holdMs: number;
  private clock = 0; private done = false; private tick = 0; private flick = 1; private lastDrawKey = '';
  constructor(private readonly host: FxHost, p: P) {
    const x = num(p, 'x', 0), y = num(p, 'y', 0), r = Math.max(12, num(p, 'radius', 44));
    this.stakes = pentagonPoints(x, y, r);
    this.pairs = allPairs(5);
    this.lines = this.pairs.map(([a, b]) => bresenham(this.stakes[a]!.x, this.stakes[a]!.y, this.stakes[b]!.x, this.stakes[b]!.y));
    const gi = num(p, 'glowIndex', -1);
    this.glowIndex = gi >= 0 && gi < this.pairs.length ? gi : southwestLineIndex(this.stakes, this.pairs);
    const speed = host.reduce ? 0.7 : 1;
    this.stakeMs = num(p, 'stakeMs', 200) * speed; this.lineMs = num(p, 'lineMs', 120) * speed; this.chantMs = num(p, 'chantMs', 500) * speed;
    this.glowMs = num(p, 'glowMs', 450); this.holdMs = num(p, 'holdMs', 1200);
    this.base = host.scene.add.graphics().setDepth(Depth.GROUND_DETAIL + 6);
    this.glow = host.scene.add.graphics().setDepth(Depth.GROUND_DETAIL + 7).setBlendMode(Phaser.BlendModes.ADD);
    this.draw();
  }
  private get total(): number { return this.stakeMs * 5 + this.lineMs * 10 + this.chantMs + this.glowMs + this.holdMs; }
  step(dt: number): boolean {
    this.clock += dt; this.tick++;
    if (this.tick % 15 === 0) this.flick = this.host.reduce ? 1 : 0.8 + this.host.rng.range(0, 0.2); // 4 fps flicker
    this.draw();
    if (!this.done && this.clock >= this.total) this.done = true;
    return this.done;
  }
  private draw(): void {
    const c = this.clock;
    const stakesPlaced = Math.min(5, Math.floor(c / this.stakeMs) + (c >= 0 ? 1 : 0));
    const lineStart = this.stakeMs * 5; const lineT = c - lineStart;
    const linesDone = lineT < 0 ? 0 : Math.min(10, Math.floor(lineT / this.lineMs));
    const partial = lineT < 0 || linesDone >= 10 ? 0 : (lineT - linesDone * this.lineMs) / this.lineMs;
    const glowStart = lineStart + this.lineMs * 10 + this.chantMs; const glowT = c - glowStart;
    const k = glowT <= 0 ? 0 : easeOutCubic(glowT / this.glowMs);
    const key = `${stakesPlaced}:${linesDone}:${Math.round(partial * 8)}:${k.toFixed(2)}:${this.flick.toFixed(2)}`;
    if (key === this.lastDrawKey) return; this.lastDrawKey = key;
    const g = this.base; g.clear();
    // Darker, higher-contrast scratched lines in the sand (with faint shadow line underneath)
    for (let i = 0; i < linesDone; i++) {
      plot(g, this.lines[i]!.map(p => ({ x: p.x, y: p.y + 1 })), 0x5a4636, 0.4);
      plot(g, this.lines[i]!, 0x8a6a42, 0.85);
    }
    if (partial > 0 && linesDone < 10) {
      const pts = this.lines[linesDone]!;
      const sub = pts.slice(0, Math.max(1, Math.round(pts.length * partial)));
      plot(g, sub.map(p => ({ x: p.x, y: p.y + 1 })), 0x5a4636, 0.4);
      plot(g, sub, 0x8a6a42, 0.85);
    }
    // wooden stakes (2×6 px with 1px dark outline, lighter top, ground shadow)
    for (let i = 0; i < stakesPlaced; i++) {
      const s = this.stakes[i]!;
      g.fillStyle(0x3a2a1e, 0.6); g.fillRect(s.x - 2, s.y, 6, 2);
      // outline
      g.fillStyle(0x2a1c14, 1); g.fillRect(s.x - 2, s.y - 7, 4, 8);
      // body
      g.fillStyle(0x8a5a32, 1); g.fillRect(s.x - 1, s.y - 6, 2, 6);
      // light top
      g.fillStyle(0xc9a26a, 1); g.fillRect(s.x - 1, s.y - 6, 2, 1);
    }
    const gl = this.glow; gl.clear();
    if (k > 0) {
      const line = this.lines[this.glowIndex]!; const a = k * this.flick;
      const halo = line.flatMap((p) => [{ x: p.x - 1, y: p.y }, { x: p.x + 1, y: p.y }, { x: p.x, y: p.y - 1 }, { x: p.x, y: p.y + 1 }]);
      plot(gl, halo, 0x1f8f8a, 0.35 * a);
      plot(gl, line, lerpColor(0x6fd3c8, 0xffffff, k * 0.7), a);
    }
  }
  finish(): void { this.clock = Math.max(this.clock, this.total); this.done = true; this.flick = 1; this.draw(); }
  destroy(): void { this.base.destroy(); this.glow.destroy(); }
}

// ------------------------------------------------------------------ ritual-triangle (P4 星塘)
/** 婆婆在地上画三角形法阵 → 三角形上方出现黑色三角形. Persistent. */
class RitualTriangle implements OneShot {
  readonly persistent = true;
  protected readonly g: Phaser.GameObjects.Graphics;
  protected readonly tri: [Pt, Pt, Pt]; protected readonly cx: number; protected readonly cy: number; protected readonly size: number;
  private readonly lineMs: number; private readonly riseMs: number; private readonly holdMs: number;
  protected clock = 0; private done = false; private tick = 0; private pulse = 0; private lastKey = '';
  constructor(protected readonly host: FxHost, p: P, instant = false) {
    this.cx = num(p, 'x', 0); this.cy = num(p, 'y', 0); this.size = Math.max(16, num(p, 'size', 40));
    this.tri = trianglePoints(this.cx, this.cy, this.size);
    const speed = host.reduce ? 0.7 : 1;
    this.lineMs = num(p, 'lineMs', 380) * speed; this.riseMs = num(p, 'riseMs', 700); this.holdMs = num(p, 'holdMs', 500);
    this.g = host.scene.add.graphics().setDepth(Depth.WORLD_BASE + this.cy - 20); // floats just behind actors at that row
    if (instant) this.clock = this.lineMs * 3 + this.riseMs;
    this.draw();
  }
  protected get total(): number { return this.lineMs * 3 + this.riseMs + this.holdMs; }
  step(dt: number): boolean {
    this.clock += dt; this.tick++;
    if (this.tick % 20 === 0) this.pulse = this.host.reduce ? 0 : (this.pulse + 1) % 3; // 3 fps breathing
    this.draw();
    if (!this.done && this.clock >= this.total) this.done = true;
    return this.done;
  }
  /** Black-triangle rise progress 0..1. */
  protected riseK(): number { const t = this.clock - this.lineMs * 3; return t <= 0 ? 0 : easeInOutSine(t / this.riseMs); }
  protected draw(): void {
    const c = this.clock; const linesDone = Math.min(3, Math.floor(c / this.lineMs)); const partial = linesDone >= 3 ? 0 : (c - linesDone * this.lineMs) / this.lineMs;
    const k = this.riseK();
    const key = `${linesDone}:${Math.round(partial * 8)}:${k.toFixed(2)}:${this.pulse}`;
    if (key === this.lastKey) return; this.lastKey = key;
    const g = this.g; g.clear();
    const [a, b, d] = this.tri; const segs = [[a, b], [b, d], [d, a]] as const;
    for (let i = 0; i < 3; i++) {
      const [s0, s1] = segs[i]!; const pts = bresenham(s0.x, s0.y, s1.x, s1.y);
      if (i < linesDone) plot(g, pts, 0x5a2a22, 0.95);
      else if (i === linesDone && partial > 0) plot(g, pts.slice(0, Math.max(1, Math.round(pts.length * partial))), 0x5a2a22, 0.95);
    }
    if (k > 0) {
      const lift = Math.round(lerp(6, 22, k)); const s = this.size * (0.78 + (this.pulse === 1 ? 0.03 : 0)); const h = s * 0.6;
      const tx = this.cx, ty = this.cy - lift;
      const p0 = { x: q(tx), y: q(ty - h * 0.6) }, p1 = { x: q(tx - s * 0.58), y: q(ty + h * 0.4) }, p2 = { x: q(tx + s * 0.58), y: q(ty + h * 0.4) };
      g.fillStyle(0x05070a, 0.92 * k); g.fillTriangle(p0.x, p0.y, p1.x, p1.y, p2.x, p2.y);
      plot(g, [...bresenham(p0.x, p0.y, p1.x, p1.y), ...bresenham(p1.x, p1.y, p2.x, p2.y), ...bresenham(p2.x, p2.y, p0.x, p0.y)], 0x2b3a5c, 0.8 * k);
    }
  }
  finish(): void { this.clock = Math.max(this.clock, this.total); this.done = true; this.draw(); }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ ritual-triangle-blackwater (P4 黑水)
interface Blob { x: number; y: number; vx: number; vy: number; age: number; life: number; size: number; alive: boolean }
/** 黑色的水不断向外涌出: dark blobs pour from the floating triangle, a pool spreads, the screen drowns. Persistent. */
class RitualTriangleBlackWater extends RitualTriangle {
  private readonly water: Phaser.GameObjects.Graphics;
  private readonly plate: Phaser.GameObjects.Rectangle;
  private readonly blobs: Blob[] = [];
  private readonly ms: number; private started = 0; private acc = 0; private wt = 0;
  constructor(host: FxHost, p: P) {
    super(host, p, true);
    this.ms = Math.max(600, num(p, 'ms', 2600));
    this.water = host.scene.add.graphics().setDepth(Depth.WORLD_BASE + this.cy - 21); // pool + blobs sit under the floating black triangle
    this.plate = host.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0x05070a).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 50).setAlpha(0);
    for (let i = 0; i < (host.reduce ? 80 : 160); i++) this.blobs.push({ x: 0, y: 0, vx: 0, vy: 0, age: 0, life: 0, size: 1, alive: false });
    this.started = this.clock;
  }
  protected override get total(): number { return this.started + this.ms; }
  override step(dt: number): boolean {
    super.step(dt);
    this.wt += dt; const u = clamp(this.wt / this.ms, 0, 1);
    const src = { x: this.cx, y: this.cy - 14 };
    this.acc += (0.6 + u * 2.6) * (this.host.reduce ? 0.5 : 1);
    while (this.acc >= 1) {
      const b = this.blobs.find((b) => !b.alive); if (!b) { this.acc = 0; break; } this.acc -= 1; // bounded: a saturated pool never banks spawns
      const a = this.host.rng.range(0, Math.PI * 2), sp = this.host.rng.range(0.6, 1.8);
      b.alive = true; b.x = src.x; b.y = src.y; b.vx = Math.cos(a) * sp; b.vy = Math.sin(a) * sp * 0.5 + 0.35; b.age = 0; b.life = this.host.rng.int(30, 70); b.size = 1;
    }
    for (const b of this.blobs) { if (!b.alive) continue; b.age++; b.x += b.vx; b.y += b.vy; b.vy += 0.04; b.vx *= 0.985; if (b.age > b.life * 0.4) b.size = b.age > b.life * 0.7 ? 3 : 2; if (b.age >= b.life) b.alive = false; }
    const w = this.water; w.clear();
    const poolR = Math.round(lerp(0, 90, easeOutCubic(u))); if (poolR > 0) { w.fillStyle(0x05070a, 0.85); w.fillEllipse(this.cx, this.cy + 6, poolR * 2, poolR); w.fillStyle(0x2b3a5c, 0.35); w.fillEllipse(this.cx - Math.round(poolR * 0.2), this.cy + 4, Math.round(poolR * 0.8), Math.round(poolR * 0.3)); }
    for (const b of this.blobs) { if (!b.alive) continue; w.fillStyle(0x05070a, 0.95); w.fillRect(q(b.x), q(b.y), b.size, b.size); if (b.size > 1 && (b.age & 4)) { w.fillStyle(0x2b3a5c, 0.9); w.fillRect(q(b.x), q(b.y), 1, 1); } }
    this.plate.setAlpha(Math.min(0.85, Math.pow(u, 2.2) * 0.9));
    return this.wt >= this.ms;
  }
  stats(): Record<string, number> { let n = 0; for (const b of this.blobs) if (b.alive) n++; return { blobs: n, blobAcc: this.acc }; }
  override finish(): void { super.finish(); this.wt = this.ms; this.plate.setAlpha(0.85); for (const b of this.blobs) b.alive = false; this.water.clear(); this.water.fillStyle(0x05070a, 0.85); this.water.fillEllipse(this.cx, this.cy + 6, 180, 90); }
  override destroy(): void { super.destroy(); this.water.destroy(); this.plate.destroy(); }
}

// ------------------------------------------------------------------ hit-spark (combat)
interface Spark { x: number; y: number; vx: number; vy: number; age: number; life: number }
class HitSpark implements OneShot {
  readonly persistent = false;
  private readonly g: Phaser.GameObjects.Graphics; private readonly sparks: Spark[] = []; private readonly color: number; private readonly x: number; private readonly y: number;
  private tick = 0; private readonly maxTicks: number;
  constructor(private readonly host: FxHost, p: P) {
    this.x = Math.round(num(p, 'x', GAME_W / 2)); this.y = Math.round(num(p, 'y', GAME_H / 2)); this.color = parseColor(p['color'], 0xfff1c0);
    const n = host.reduce ? 6 : num(p, 'count', 11); this.maxTicks = host.reduce ? 12 : 20;
    for (let i = 0; i < n; i++) { const a = host.rng.range(0, Math.PI * 2), s = host.rng.range(1.4, 3.2); this.sparks.push({ x: this.x, y: this.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s * 0.7 - 0.6, age: 0, life: host.rng.int(10, this.maxTicks) }); }
    this.g = host.scene.add.graphics().setDepth(Depth.OVERHEAD - 1).setBlendMode(Phaser.BlendModes.ADD);
  }
  step(): boolean {
    this.tick++;
    const g = this.g; g.clear();
    if (this.tick <= 2) { g.fillStyle(0xffffff, this.tick === 1 ? 0.95 : 0.5); g.fillRect(this.x - 3, this.y, 7, 1); g.fillRect(this.x, this.y - 3, 1, 7); }
    for (const s of this.sparks) {
      if (s.age >= s.life) continue; s.age++; s.x += s.vx; s.y += s.vy; s.vy += 0.12; s.vx *= 0.96;
      const k = 1 - s.age / s.life; g.fillStyle(lerpColor(0xffb347, this.color, k), 0.4 + 0.6 * k); const sz = k > 0.5 ? 2 : 1; g.fillRect(q(s.x), q(s.y), sz, sz);
    }
    return this.tick >= this.maxTicks;
  }
  finish(): void { this.tick = this.maxTicks; this.g.clear(); }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ slash-arc (combat)
class SlashArc implements OneShot {
  readonly persistent = false;
  private readonly g: Phaser.GameObjects.Graphics; private readonly arc: Pt[]; private tick = 0;
  private readonly frames: number; private readonly color: number; private readonly trail: number;
  constructor(host: FxHost, p: P) {
    const x = num(p, 'x', GAME_W / 2), y = num(p, 'y', GAME_H / 2), r = Math.max(6, num(p, 'radius', 18));
    const dir = str(p, 'dir', 'right'); const mid = typeof p['angle'] === 'number' ? (p['angle'] as number) : (FACING_ANGLE[dir] ?? 0);
    const span = (num(p, 'span', 110) * Math.PI) / 180; const flip = bool(p, 'flip', false) ? -1 : 1;
    this.arc = arcPoints(x, y, r, mid - (span / 2) * flip, mid + (span / 2) * flip, 22);
    this.frames = host.reduce ? 4 : 6; this.color = parseColor(p['color'], 0xffffff); this.trail = parseColor(p['trail'], 0xbef2ec);
    this.g = host.scene.add.graphics().setDepth(Depth.OVERHEAD - 1).setBlendMode(Phaser.BlendModes.ADD);
  }
  step(): boolean {
    this.tick++;
    const g = this.g; g.clear();
    const total = this.frames + 4; const u = clamp(this.tick / this.frames, 0, 1); const head = Math.round(this.arc.length * u);
    const fade = this.tick > this.frames ? 1 - (this.tick - this.frames) / 4 : 1;
    const tail = Math.max(0, head - Math.round(this.arc.length * 0.55));
    for (let i = tail; i < head; i++) {
      const p = this.arc[i]!; const k = (i - tail) / Math.max(1, head - tail);
      g.fillStyle(lerpColor(this.trail, this.color, k), (0.25 + 0.75 * k) * fade); g.fillRect(p.x, p.y, 1, 1);
      if (k > 0.5) { g.fillStyle(this.color, 0.6 * k * fade); g.fillRect(p.x, p.y - 1, 1, 1); g.fillRect(p.x + 1, p.y, 1, 1); }
    }
    return this.tick >= total;
  }
  finish(): void { this.tick = this.frames + 4; this.g.clear(); }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ omen-glitch (P13 不祥预感)
interface OmenFilters { pixelate: Phaser.Filters.Pixelate | null; color: Phaser.Filters.ColorMatrix | null; vignette: Phaser.Filters.Vignette | null; cam: Phaser.Cameras.Scene2D.Camera }
/** 像素化异常 + 色彩抽离 + 视野收束. The colour grade and vignette stay (persistent); pixel bursts stop. */
class OmenGlitch implements OneShot {
  readonly persistent = true;
  private readonly ms: number; private readonly strength: number; private readonly bursts: GlitchBurst[]; private readonly bars: Phaser.GameObjects.Graphics;
  private clock = 0; private done = false; private f: OmenFilters | null = null; private barsKey = -1;
  constructor(private readonly host: FxHost, p: P) {
    this.ms = Math.max(200, num(p, 'ms', 2800)); this.strength = clamp(num(p, 'strength', 0.6), 0, 1);
    this.bursts = host.reduce ? [] : glitchSchedule(host.rng, this.ms, this.strength);
    this.bars = screenGraphics(host.scene, Depth.WEATHER + 6);
    const cam = host.scene.cameras.main as Phaser.Cameras.Scene2D.Camera & { filters?: Phaser.Types.GameObjects.FiltersInternalExternal };
    try {
      if (cam.filters) {
        const prev = host.shared.get('omen-filters') as OmenFilters | undefined;
        if (prev && prev.cam === cam) this.f = prev;
        else {
          this.f = { cam, pixelate: host.reduce ? null : cam.filters.internal.addPixelate(0), color: cam.filters.internal.addColorMatrix(), vignette: cam.filters.external.addVignette(0.5, 0.5, 0.95, 0) };
          this.f.pixelate?.setActive(false);
          host.shared.set('omen-filters', this.f);
        }
      }
    } catch (e) { console.warn('[effects] omen filters unavailable', e); this.f = null; }
  }
  step(dt: number): boolean {
    this.clock += dt; const u = clamp(this.clock / this.ms, 0, 1);
    const grade = easeInOutSine(u) * this.strength;
    if (this.f) {
      if (this.f.color) { const cm = this.f.color.colorMatrix; cm.reset(); cm.saturate(-0.75 * grade); cm.contrast(0.18 * grade, true); cm.brightness(1 - 0.12 * grade, true); }
      if (this.f.vignette) { this.f.vignette.radius = lerp(0.95, 0.55, grade); this.f.vignette.strength = lerp(0, 0.85, grade); }
      const burst = this.bursts.find((b) => this.clock >= b.at && this.clock < b.at + b.dur);
      if (this.f.pixelate) { if (burst) { this.f.pixelate.amount = burst.amount; if (!this.f.pixelate.active) this.f.pixelate.setActive(true); } else if (this.f.pixelate.active) this.f.pixelate.setActive(false); }
      this.drawBars(burst ? this.bursts.indexOf(burst) : -1);
    } else this.drawBars(-1);
    if (!this.done && this.clock >= this.ms) { this.done = true; this.drawBars(-1); if (this.f?.pixelate?.active) this.f.pixelate.setActive(false); }
    return this.done;
  }
  private drawBars(idx: number): void {
    if (idx === this.barsKey) return; this.barsKey = idx;
    const g = this.bars; g.clear(); if (idx < 0) return;
    const n = 2 + (idx % 3);
    for (let i = 0; i < n; i++) { const y = this.host.rng.int(0, GAME_H - 2), h = this.host.rng.int(1, 2); g.fillStyle(0x05070a, 0.35); g.fillRect(0, y, GAME_W, h); g.fillStyle(0xd6ecf2, 0.12); g.fillRect(this.host.rng.int(0, GAME_W / 2), y, this.host.rng.int(40, GAME_W / 2), 1); }
  }
  finish(): void { this.clock = this.ms; this.step(0); }
  destroy(): void {
    this.bars.destroy();
    if (this.f && this.host.shared.get('omen-filters') === this.f) {
      this.host.shared.delete('omen-filters');
      try { const fl = this.f.cam.filters; if (this.f.pixelate) fl.internal.remove(this.f.pixelate); if (this.f.color) fl.internal.remove(this.f.color); if (this.f.vignette) fl.external.remove(this.f.vignette); } catch { /* camera gone */ }
    }
    this.f = null;
  }
}

// ------------------------------------------------------------------ fade-color / dusk-tint (screen plate)
/** Full-screen colour plate. `out: true` fades the plate in and keeps it; `out: false` fades the existing plate away. */
class FadeColor implements OneShot {
  readonly persistent: boolean;
  private readonly rect: Phaser.GameObjects.Rectangle; private readonly ms: number; private readonly from: number; private readonly to: number; private readonly out: boolean;
  private clock = 0; private done = false; private readonly key: string;
  constructor(private readonly host: FxHost, p: P, defaults: { color: number; alpha: number; ms: number; key: string }) {
    this.key = str(p, 'key', defaults.key); this.out = bool(p, 'out', true);
    this.ms = Math.max(0, num(p, 'ms', defaults.ms)); const alpha = clamp(num(p, 'alpha', defaults.alpha), 0, 1); const color = parseColor(p['color'], defaults.color);
    const existing = host.shared.get(`plate:${this.key}`) as Phaser.GameObjects.Rectangle | undefined;
    if (existing && existing.active) { this.rect = existing; this.rect.setFillStyle(color, 1); }
    else { this.rect = host.scene.add.rectangle(0, 0, GAME_W, GAME_H, color).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 400).setAlpha(0); host.shared.set(`plate:${this.key}`, this.rect); }
    this.from = this.rect.alpha; this.to = this.out ? alpha : 0; this.persistent = this.out;
    if (this.ms === 0) { this.rect.setAlpha(this.to); this.done = true; }
  }
  step(dt: number): boolean {
    if (this.done) return true;
    this.clock += dt; const u = clamp(this.clock / this.ms, 0, 1);
    this.rect.setAlpha(lerp(this.from, this.to, easeInOutSine(u)));
    if (u >= 1) this.done = true;
    return this.done;
  }
  finish(): void { this.rect.setAlpha(this.to); this.done = true; }
  destroy(): void {
    // The plate belongs to the director (shared); an "in" fade removes it, an "out" fade keeps it until detach.
    if (!this.out || !this.rect.active) { if (this.host.shared.get(`plate:${this.key}`) === this.rect) this.host.shared.delete(`plate:${this.key}`); this.rect.destroy(); }
  }
}

// ------------------------------------------------------------------ door-close (P4 关门)
class DoorClose implements OneShot {
  readonly persistent = false;
  private readonly g: Phaser.GameObjects.Graphics; private readonly ms: number; private clock = 0;
  constructor(host: FxHost, p: P) { this.ms = Math.max(200, num(p, 'ms', 900)); this.g = screenGraphics(host.scene, Depth.LIGHTING + 450); }
  step(dt: number): boolean {
    this.clock += dt; const u = clamp(this.clock / this.ms, 0, 1);
    const w = u < 0.45 ? easeInOutSine(u / 0.45) : u < 0.6 ? 1 : 1 - easeInOutSine((u - 0.6) / 0.4);
    const half = Math.round((GAME_W / 2) * w);
    const g = this.g; g.clear();
    if (half > 0) { g.fillStyle(0x05070a, 1); g.fillRect(0, 0, half, GAME_H); g.fillRect(GAME_W - half, 0, half, GAME_H); g.fillStyle(0x3a2a1e, 1); g.fillRect(half - 1, 0, 1, GAME_H); g.fillRect(GAME_W - half, 0, 1, GAME_H); }
    return u >= 1;
  }
  finish(): void { this.clock = this.ms; this.g.clear(); }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ freeze-frame (P13 定格)
class FreezeFrame implements OneShot {
  readonly persistent = false;
  private readonly rect: Phaser.GameObjects.Rectangle; private readonly ms: number; private clock = 0;
  constructor(host: FxHost, p: P) {
    this.ms = Math.max(0, num(p, 'ms', 1800));
    this.rect = host.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0xf4f8ff).setOrigin(0).setScrollFactor(0).setDepth(Depth.WEATHER + 7).setAlpha(host.reduce ? 0.12 : 0.3);
  }
  step(dt: number): boolean { this.clock += dt; this.rect.setAlpha(Math.max(0, 0.3 * (1 - this.clock / 220))); return this.clock >= this.ms; }
  finish(): void { this.clock = this.ms; this.rect.setAlpha(0); }
  destroy(): void { this.rect.destroy(); }
}

// ------------------------------------------------------------------ instant helpers
class Instant implements OneShot {
  readonly persistent = false;
  constructor(run: () => void) { try { run(); } catch (e) { console.warn('[effects] instant effect failed', e); } }
  step(): boolean { return true; }
  finish(): void { /* nothing */ }
  destroy(): void { /* nothing */ }
}

/** Registry: effect name → factory. Names used by content timelines are listed in docs/progress/audio-fx-builder.md. */
export const ONE_SHOTS: Record<string, OneShotFactory> = {
  'ritual-pentagon': (h, p) => new RitualPentagon(h, p),
  'ritual-triangle': (h, p) => new RitualTriangle(h, p),
  'ritual-triangle-blackwater': (h, p) => new RitualTriangleBlackWater(h, p),
  'hit-spark': (h, p) => new HitSpark(h, p),
  'slash-arc': (h, p) => new SlashArc(h, p),
  'omen-glitch': (h, p) => new OmenGlitch(h, p),
  'fade-color': (h, p) => new FadeColor(h, p, { color: 0x000000, alpha: 1, ms: 600, key: 'fade' }),
  'dusk-tint': (h, p) => new FadeColor(h, { ...p, out: true }, { color: 0xc46a3a, alpha: 0.28, ms: 2500, key: 'tint' }),
  'door-close': (h, p) => new DoorClose(h, p),
  'freeze-frame': (h, p) => new FreezeFrame(h, p),
  /** 环境音收窄: ducks music + ambience for the omen beat (`duck` = level to fall to, default 0.35). */
  'omen-ambience': (h, p) => new Instant(() => h.duck(clamp(1 - num(p, 'duck', 0.35), 0, 1), num(p, 'ms', 2800))),
  lightning: (h) => new Instant(() => h.lightning()),
};

export const ONE_SHOT_NAMES: readonly string[] = Object.keys(ONE_SHOTS);
